import { useMemo } from 'react';
import { useGlobalStoreSelector } from '@/stores/global.store';
import { PlansUnion } from '@/stores/types';

export type PlanFeature = 'standard' | 'recording' | 'drawing' | 'screenshot' | 'geolocation' | 'apiAccess';

const STANDARD_PLUS_PLANS = new Set<PlansUnion>(['TRIAL', 'STANDARD', 'STANDARD_YEARLY', 'PREMIUM', 'PREMIUM_YEARLY']);

const PREMIUM_ONLY_PLANS = new Set<PlansUnion>(['TRIAL', 'PREMIUM', 'PREMIUM_YEARLY']);

const FEATURE_PLAN_MAP: Record<PlanFeature, Set<PlansUnion>> = {
	standard: STANDARD_PLUS_PLANS,
	recording: STANDARD_PLUS_PLANS,
	drawing: STANDARD_PLUS_PLANS,
	screenshot: STANDARD_PLUS_PLANS,
	geolocation: STANDARD_PLUS_PLANS,
	apiAccess: PREMIUM_ONLY_PLANS,
};

export function hasPlanFeature(plan: PlansUnion | undefined, feature: PlanFeature): boolean {
	return FEATURE_PLAN_MAP[feature].has(plan ?? 'FREE');
}

export function useFeatureAccess() {
	const plan = useGlobalStoreSelector((s) => s.currentCompany?.companyBillingEntity?.plan);
	const publicMeetingDrawing = useGlobalStoreSelector((s) => s.publicMeetingFeatureAccess.hasDrawing);

	return useMemo(
		() => ({
			hasStandardPlan: hasPlanFeature(plan, 'standard'),
			hasRecording: hasPlanFeature(plan, 'recording'),
			// Logged-in users: company plan. Guests / clients in meeting: host plan from branding API.
			hasDrawing: hasPlanFeature(plan, 'drawing') || publicMeetingDrawing,
			hasScreenshot: hasPlanFeature(plan, 'screenshot'),
			hasGeolocation: hasPlanFeature(plan, 'geolocation'),
			hasApiAccess: hasPlanFeature(plan, 'apiAccess'),
			canAccess: (feature: PlanFeature) =>
				feature === 'drawing' ? hasPlanFeature(plan, 'drawing') || publicMeetingDrawing : hasPlanFeature(plan, feature),
		}),
		[plan, publicMeetingDrawing],
	);
}
